import { ArrowUpRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { HomeCardImage } from './HomeCardImage'
import { LocaleLink } from '@/components/LocaleLink'
import type { ReactNode } from 'react'

import { cn } from '@/utilities/ui'

import { CategoriaComSelo } from './CategoriaComSelo'
import { ReadTime, externalArticleLink, SectionHeading, type SectionHeadingLevel } from './common'
import { HomeBiNewsStoryRow } from './HomeBiNewsStoryRow'
import { HomeSectionCategoryNav } from './HomeSectionCategoryNav'
import {
  HOME_BI_NEWS_IMAGE_HOVER,
  HOME_BI_NEWS_ITEM_GROUP,
  HOME_BI_NEWS_TITLE_LINK,
  HOME_BI_SECTION,
  HOME_BI_SECTION_HEADING,
  HOME_MIDDLE_COL_GUTTER,
  HOME_MIDDLE_NEWS_GRID,
} from './home-bi-layout'
import type { DestaquePublicacao, HomeSectionNavItem } from './types'

export type HomeMiddleNavItem = HomeSectionNavItem

function buildDefaultHomeMiddleNav(
  t: ReturnType<typeof useTranslations<'Home'>>,
): HomeMiddleNavItem[] {
  return [
    { id: 'todas', label: t('navAll') },
    { id: 'tech', label: t('navTech'), href: '#tech' },
    { id: 'mercados', label: t('navMarkets'), href: '#mercados' },
    { id: 'politica', label: t('navPolitics'), href: '#politica' },
    { id: 'negocios', label: t('navBusiness'), href: '#negocios' },
  ]
}

function PrincipalColumnMiddle({
  principal,
  priorityImageLoad = true,
  showThumbnail = true,
}: {
  principal: DestaquePublicacao
  priorityImageLoad?: boolean
  showThumbnail?: boolean
}) {
  return (
    <div data-hero-column="principal" className="min-w-0">
      <article className={cn('min-w-0', HOME_BI_NEWS_ITEM_GROUP)}>
        {showThumbnail ? (
          <LocaleLink
            {...externalArticleLink}
            href={principal.href}
            className="relative block h-[320px] w-full overflow-hidden rounded-(--post-thumb-radius,0px) focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none lg:h-[330px]"
            tabIndex={-1}
            aria-hidden
          >
            <HomeCardImage
              src={principal.img}
              srcSizes={principal.imgSizes}
              alt={principal.alt}
              className={HOME_BI_NEWS_IMAGE_HOVER}
              sizes="(min-width: 1024px) 496px, 100vw"
              priority={priorityImageLoad}
              fetchPriority={priorityImageLoad ? 'high' : undefined}
            />
          </LocaleLink>
        ) : null}
        <div className={cn('min-w-0', showThumbnail && 'mt-3')}>
          <CategoriaComSelo item={principal} className="mb-1" />

          <h3 className="text-lg leading-snug font-semibold tracking-tight lg:text-xl">
            <LocaleLink
              {...externalArticleLink}
              href={principal.href}
              className={HOME_BI_NEWS_TITLE_LINK}
            >
              {principal.title}
            </LocaleLink>
          </h3>
          <ReadTime>{principal.read}</ReadTime>
        </div>
      </article>
    </div>
  )
}

function FascinatingColumnMiddle({
  lista,
  showThumbnail = true,
}: {
  lista: DestaquePublicacao[]
  showThumbnail?: boolean
}) {
  return (
    <div
      data-hero-column="fascinating"
      aria-labelledby="home-middle-news-heading"
      className="w-full min-w-0"
    >
      <div className="flex w-full min-w-0 flex-col">
        {lista.map((item, index) => (
          <HomeBiNewsStoryRow
            key={`${item.href}-${index}`}
            item={item}
            index={index}
            showThumbnail={showThumbnail}
          />
        ))}
      </div>
    </div>
  )
}

export type HomeMiddleDestaqueProps = {
  /** Itens: [0]=manchete (h-320), resto=lista compact. */
  items: DestaquePublicacao[]
  className?: string
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreTopicLabel?: string
  moreHref?: string
  navItems?: HomeMiddleNavItem[]
  /** Quando fornecido, substitui a nav estática de categorias pelo filtro in-place (Header Filter). */
  filterSlot?: ReactNode
  priorityImageLoad?: boolean
  showThumbnail?: boolean
}

/**
 * Destaque da faixa `home-middle`: grelha 2 colunas (sem carrossel),
 * manchete grande + demais histórias em fila título|imagem.
 * Server Component (a nav de categorias é o único client).
 */
export function HomeMiddleDestaque({
  items,
  className,
  sectionHeading,
  sectionHeadingLevel = 'h2',
  showSectionHeader = true,
  showMoreLink = true,
  moreTopicLabel,
  moreHref = '/business-insider',
  navItems,
  filterSlot,
  priorityImageLoad = true,
  showThumbnail = true,
}: HomeMiddleDestaqueProps) {
  const t = useTranslations('Home')
  if (!items || items.length === 0) return null
  const heading = sectionHeading || t('moreNews')
  const [principal, ...lista] = items
  const sectionId = HOME_BI_SECTION.middle
  const headingId = 'home-middle-news-heading'

  return (
    <div
      data-region="home-middle-destaque"
      data-home-section={sectionId}
      aria-labelledby={showSectionHeader ? headingId : undefined}
      className={cn(className)}
    >
      <div
        className={cn(HOME_MIDDLE_NEWS_GRID, 'lg:items-start')}
        data-home-layout="news-two-column"
      >
        <div className="lg:col-span-2 lg:pr-5 xl:pr-6" data-home-region="news-heading-block">
          {showSectionHeader ? (
            <header className="section-heading flex items-center justify-between gap-4">
              <SectionHeading
                level={sectionHeadingLevel}
                id={headingId}
                className={cn('heading min-w-0 pr-3', HOME_BI_SECTION_HEADING)}
              >
                {heading}
              </SectionHeading>
              {showMoreLink ? (
                <LocaleLink
                  href={moreHref}
                  data-component-type="cta-more-link-arrow"
                  className="cta-more-link-arrow group inline-flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-sm py-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <span className="link-text text-xs leading-none font-semibold tracking-wide whitespace-nowrap">
                    {t('viewMore')}
                    {moreTopicLabel ? (
                      <span className="link-text-label">&nbsp;{moreTopicLabel}</span>
                    ) : null}
                  </span>
                  <span
                    className="icon-wrapper bg-foreground text-background flex size-6 shrink-0 items-center justify-center rounded-full transition-opacity group-hover:opacity-90"
                    aria-hidden
                  >
                    <ArrowUpRight className="size-3" strokeWidth={2.25} />
                  </span>
                </LocaleLink>
              ) : null}
            </header>
          ) : null}

          {filterSlot ?? (
            <HomeSectionCategoryNav
              items={navItems ?? buildDefaultHomeMiddleNav(t)}
              className="mt-3 lg:mt-4 lg:pb-4"
            />
          )}
        </div>

        <div
          data-home-column="primary"
          data-home-section={sectionId}
          className="min-w-0 lg:row-start-2 lg:pt-5 lg:pr-8"
        >
          <PrincipalColumnMiddle
            principal={principal}
            priorityImageLoad={priorityImageLoad}
            showThumbnail={showThumbnail}
          />
        </div>

        <div
          data-home-column="secondary"
          data-home-section={sectionId}
          className={cn(
            'min-w-0 lg:row-start-2 lg:border-border lg:border-l lg:pt-5',
            HOME_MIDDLE_COL_GUTTER,
          )}
        >
          <FascinatingColumnMiddle
            lista={lista}
            showThumbnail={showThumbnail}
          />
        </div>
      </div>
    </div>
  )
}
